// JSON-LD for the `referral_link` and `referral_banner` surfaces — both
// share the same canonical URL `/r/[code]` and the same OG image, so a
// single builder serves both.
//
// Schema choice: WebPage with a Person `mainEntity` (the referrer).
// Pages that have already fetched the referrer's profile pass `name`
// and `image` via `ctx` so the Person node is meaningful; otherwise we
// emit a minimal WebPage.

import type { ShareSurface } from '@/lib/share/events';

import { resolveCanonicalOrigin } from '../origin';
import { organizationId, websiteId } from './site';
import type { JsonLdBuilderContext, JsonLdPayload } from './types';

export function buildReferralJsonLd(
  surface: Extract<ShareSurface, { kind: 'referral_link' | 'referral_banner' }>,
  ctx: JsonLdBuilderContext,
): JsonLdPayload {
  const referrerName = ctx.name;
  const personUrl = `${resolveCanonicalOrigin()}/portfolio/${surface.userId}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${ctx.pageUrl}#webpage`,
      url: ctx.pageUrl,
      name:
        referrerName !== undefined ? `${referrerName} invited you to Morfi` : 'Join me on Morfi',
      ...(ctx.description !== undefined ? { description: ctx.description } : {}),
      primaryImageOfPage: { '@type': 'ImageObject', url: ctx.ogImageUrl },
      isPartOf: { '@id': websiteId() },
      publisher: { '@id': organizationId() },
      ...(referrerName !== undefined
        ? {
            mainEntity: {
              '@type': 'Person',
              '@id': `${personUrl}#person`,
              name: referrerName,
              url: personUrl,
            },
          }
        : {}),
    },
  ];
}
